import { useState } from 'react';
import { Box, Card, CardContent, Chip, Collapse, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as EmptyIcon,
  Error as AttentionIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import { navigate } from '../lib/navigation';

const statusColor = { Complete: 'success', 'Needs attention': 'warning', 'Not started': 'default' };

function StatusIcon({ status }) {
  if (status === 'Complete') return <CheckIcon color="success" fontSize="small" />;
  if (status === 'Needs attention') return <AttentionIcon color="warning" fontSize="small" />;
  return <EmptyIcon color="disabled" fontSize="small" />;
}

function CompletionRow({ section, onOpen }) {
  const actionable = section.status !== 'Complete';
  return (
    <Stack
      direction="row"
      spacing={1}
      role={actionable ? 'link' : undefined}
      tabIndex={actionable ? 0 : undefined}
      onClick={actionable ? () => onOpen(section.path) : undefined}
      onKeyDown={actionable ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(section.path);
        }
      } : undefined}
      sx={{
        alignItems: 'center',
        p: 1,
        mx: -1,
        borderRadius: 2,
        border: '1px solid transparent',
        ...(actionable && {
          cursor: 'pointer',
          '&:hover, &:focus-visible': { bgcolor: 'action.hover', outline: 'none' },
        }),
        ...(section.status === 'Needs attention' && {
          bgcolor: 'rgba(237, 108, 2, 0.08)',
          borderColor: 'rgba(237, 108, 2, 0.28)',
          '&:hover, &:focus-visible': { bgcolor: 'rgba(237, 108, 2, 0.14)', outline: 'none' },
        }),
      }}
    >
      <StatusIcon status={section.status} />
      <div style={{ flexGrow: 1 }}>
        <Typography variant="body2">{section.label}</Typography>
        {section.hint && (
          <Typography variant="caption" sx={{ display: 'block', color: 'grey.600', fontSize: '0.75rem', fontStyle: 'italic' }}>
            {section.hint}
          </Typography>
        )}
      </div>
      <Chip size="small" label={section.status} color={statusColor[section.status]} variant={section.status === 'Complete' ? 'filled' : 'outlined'} />
    </Stack>
  );
}

function ProfileCompletionPanel({ sections, loading }) {
  const [expanded, setExpanded] = useState(true);
  const [optionalExpanded, setOptionalExpanded] = useState(false);
  const requiredSections = sections.filter((section) => section.required);
  const optionalSections = sections.filter((section) => !section.required);
  const completed = requiredSections.filter((section) => section.status === 'Complete').length;
  const optionalCompleted = optionalSections.filter((section) => section.status === 'Complete').length;
  const openSection = (path) => {
    const targetID = path.split('#')[1];
    navigate(path);
    if (targetID) setTimeout(() => document.getElementById(targetID)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <div>
            <Typography variant="h6">Complete your profile</Typography>
            <Typography variant="body2" color="text.secondary">
              {completed} of {requiredSections.length} required sections complete
            </Typography>
          </div>
          <Tooltip title={expanded ? 'Collapse checklist' : 'Expand checklist'}>
            <IconButton
              onClick={() => setExpanded((current) => !current)}
              aria-label={expanded ? 'Collapse profile checklist' : 'Expand profile checklist'}
              aria-expanded={expanded}
              size="small"
            >
              <ExpandIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <LinearProgress variant={loading ? 'indeterminate' : 'determinate'} value={(completed / requiredSections.length) * 100} sx={{ mb: 2 }} />
        <Collapse in={expanded} unmountOnExit>
          <Stack spacing={0.75}>
            {requiredSections.map((section) => <CompletionRow key={section.key} section={section} onOpen={openSection} />)}

            <Box sx={{ mt: 1, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Stack
                direction="row"
                spacing={1}
                role="button"
                tabIndex={0}
                aria-expanded={optionalExpanded}
                onClick={() => setOptionalExpanded((current) => !current)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setOptionalExpanded((current) => !current);
                  }
                }}
                sx={{ alignItems: 'center', cursor: 'pointer', borderRadius: 2, p: 1, mx: -1, '&:hover, &:focus-visible': { bgcolor: 'action.hover', outline: 'none' } }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={700}>Improve your profile</Typography>
                  <Typography sx={{ color: 'grey.600', fontSize: '0.7rem', lineHeight: 1.4 }}>
                    Optional details improve your profile quality.
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${optionalCompleted}/${optionalSections.length} complete`}
                  sx={{
                    flexShrink: 0,
                    height: 24,
                    bgcolor: 'rgba(78, 40, 181, 0.08)',
                    color: 'primary.main',
                    fontWeight: 700,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
                <ExpandIcon sx={{ transform: optionalExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }} />
              </Stack>
              <Collapse in={optionalExpanded} unmountOnExit>
                <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                  {optionalSections.map((section) => <CompletionRow key={section.key} section={section} onOpen={openSection} />)}
                </Stack>
              </Collapse>
            </Box>
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default ProfileCompletionPanel;
